<template>
  <div class="sc-form-select-container" :class="[marginClasses, sizeClasses]">
    <BaseLabel v-if="showLabel" :size="size" :for="id">{{ label }}</BaseLabel>
    <div
      class="sc-select-box"
      :class="{ 'has-error': error && !value.length, disabled: disabledClass }"
    >
      <div
        :ref="`${computedName}-sc-selectbox`"
        class="sc-select-button brd"
        :class="{
          'open-select-box': showOptions,
        }"
        :style="`z-index:${highestZIndex + (showOptions ? 5 : 3)}`"
      >
        <div class="sc-selected-value">
          <input
            class="sc-select-input"
            type="text"
            v-model="select_query"
            :placeholder="placeholder"
            autocomplete="new-off"
            @focus="showOption"
            @click="showOption"
            :readonly="!showOptions"
            @keyup.enter="deleteEmpty()"
          />
          <transition
            enter-active-class="fadeIn"
            leave-active-class="fadeOut"
            mode="out-in"
          >
            <div
              v-if="error && !value.length"
              class="input-error"
              style="z-index: 1"
            >
              <img :src="`${scBaseUrl}/assets/images/Error.svg`" alt="Error" />
            </div>
          </transition>
          <transition
            enter-active-class="fadeIn"
            leave-active-class="fadeOut"
            mode="out-in"
          >
            <div v-if="success" class="input-checked">
              <img
                :src="`${scBaseUrl}/assets/images/Checked.svg`"
                alt="Checked"
              />
            </div>
          </transition>
        </div>
        <div class="sc-select-chevrons">
          <img :src="`${scBaseUrl}/assets/images/symbols-arrow.png`" alt />
        </div>
      </div>
      <transition
        enter-active-class="fadeInTop"
        leave-active-class="fadeOut"
        mode="out-in"
      >
        <div
          :id="`${computedName}-sc-options`"
          v-show="showOptions"
          v-closable="{
            exclude: [`${computedName}-sc-selectbox`],
            handler: 'onClose',
          }"
          class="sc-options"
          :style="`z-index:${highestZIndex + 4}`"
        >
          <template v-if="availableOptions.length">
            <div
              v-if="selectedOption"
              class="sc-option sc-selected"
              :key="id + '_' + selectedOption[optionLabel]"
            >
              <span class="sc-select-option-label">
                {{
                  optionLabel
                    ? selectedOption[optionLabel]
                    : selectedOption | TitleString
                }}
                <span
                  class="sc-select-option-sub-label"
                  v-if="optionSubLabel"
                  >{{
                    optionSubLabel
                      ? selectedOption[optionSubLabel]
                      : "" | UpperString
                  }}</span
                >
              </span>
            </div>

            <div
              class="sc-option"
              :class="{
                'sc-selected':
                  selectedOption &&
                  selectedOption[optionValue] == option[optionValue],
              }"
              v-for="(option, index) in availableOptions"
              :key="id + '_' + index + '_' + option[optionLabel]"
              v-if="
                selectedOption &&
                selectedOption[optionValue] != option[optionValue]
              "
              @click="handleInput(index)"
            >
              <span class="sc-select-option-label">
                {{ optionLabel ? option[optionLabel] : option | TitleString }}
                <span
                  class="sc-select-option-sub-label"
                  v-if="optionSubLabel"
                  >{{
                    optionSubLabel ? option[optionSubLabel] : "" | UpperString
                  }}</span
                >
              </span>
            </div>
          </template>
          <template v-else>
            <div class="sc-option">
              <span class="sc-select-option-label">Nothing found</span>
            </div>
          </template>
        </div>
      </transition>
    </div>

    <slot></slot>
  </div>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number, Object, Boolean],
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "Please select one",
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    optionLabel: {
      type: String,
      default: "label",
    },
    optionValue: {
      type: String,
      default: "value",
    },
    optionInput: {
      type: String,
      default: "",
    },
    optionSubLabel: {
      type: String,
      default: "",
    },
    options: {
      required: true,
      type: Array,
    },
    size: {
      default: "w-full",
      type: String,
    },
    margin: {
      default: "",
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    form: {
      default: null,
      type: Object,
    },
    scrollHeight: {
      default: 100,
      type: Number,
    },
    scrollMultiplier: {
      default: 6,
      type: Number,
    },
  },
  data() {
    return {
      selectedOption: "",
      showOptions: false,
      select_query: "",
      chosen: false,
    };
  },
  mounted() {
    this.selectedOption = this.options.find(
      (option) => option[this.optionValue] == this.value
    );
  },
  computed: {
    sizeClasses() {
      return this.size;
    },
    computedName() {
      if (this.name) {
        return this.name;
      } else if (this.id) {
        return this.id;
      } else {
        return "";
      }
    },
    marginClasses() {
      return this.margin;
    },
    disabledClass() {
      if (this.value.length) {
        return false;
      }
      return true;
    },
    availableOptions() {
      let options = this.options || [];
      if (!this.select_query || !this.select_query.length) {
        return options;
      }
      return options.filter((option) => {
        return `${option[this.optionLabel]} ${option[this.optionSubLabel]}`
          .toLowerCase()
          .includes(this.select_query.toLowerCase());
      });
    },
  },
  watch: {
    selectedOption() {
      this.select_query = this.$options.filters.TitleString(
        this.selectedOption && this.selectedOption[this.optionLabel]
          ? this.selectedOption[
              this.optionInput.length ? this.optionInput : this.optionLabel
            ]
          : ""
      );
    },
    value() {
      this.selectedOption = this.options.find(
        (option) => option[this.optionValue] == this.value
      );
    },
    showOptions(showingOption) {
      if (!showingOption && this.selectedOption) {
        this.select_query = this.$options.filters.TitleString(
          this.selectedOption && this.selectedOption[this.optionLabel]
            ? this.selectedOption[
                this.optionInput.length ? this.optionInput : this.optionLabel
              ]
            : ""
        );
      }
    },
  },
  methods: {
    handleInput(index) {
      //   Here index is the index of the selected object
      this.chosen = true;
      this.selectedOption = this.availableOptions[index];
      const valueToEmit = this.optionValue
        ? this.selectedOption[this.optionValue]
        : this.selectedOption;
      this.$emit("input", valueToEmit);
      this.$emit("change");
      if (this.selectedOption) {
        let valuetoInput = this.optionInput.length
          ? this.optionInput
          : this.optionLabel;
        this.select_query = this.selectedOption[valuetoInput];
      }
      this.onClose();
    },
    deleteEmpty() {
      setTimeout(() => {
        if (this.select_query && this.select_query.length) {
          if (!this.chosen) {
            this.handleInput(0);
          }
        } else {
          if (this.selectedOption) {
            let valueToEmit = "";
            valueToEmit = this.optionValue
              ? this.selectedOption[this.optionValue]
              : this.selectedOption;
            this.$emit("input", valueToEmit);
            let valuetoInput = this.optionInput.length
              ? this.optionInput
              : this.optionLabel;
            this.select_query = this.selectedOption[valuetoInput];
          }
        }
      }, 100);

      this.onClose();
    },
    showOption() {
      this.select_query = "";
      this.showOptions = true;
    },
    onClose() {
      this.showOptions = false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// BaseSelect.vue?12f8e0a8